import { useEffect, useMemo, useRef, useState } from 'react';

import { DashboardProjectSelector } from '@/features/dashboard/components/dashboard/dashboard-project-selector';
import { DashboardProjectSlotCard } from '@/features/dashboard/components/dashboard/dashboard-project-slot-card';
import { EmptyProjectSlideCards } from '@/features/dashboard/components/dashboard/empty-project-slide-cards';
import { ProjectSlideCardEmpty } from '@/features/dashboard/components/dashboard/project-slide-card-empty';
import { useDashboardProjectSlots } from '@/features/dashboard/hooks/use-dashboard-project-slots';
import { useProjects } from '@/features/projects/shared/hooks/use-projects';
import { ProjectWithContractors } from '@/shared/types/api';

export const ProjectSlideCards = () => {
  const [cardIndices, setCardIndices] = useState([0, 0, 0, 0]);
  const [isPaused, setIsPaused] = useState([false, false, false, false]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const allPaused = isPaused.every((paused) => paused);

    if (allPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setCardIndices((prev) =>
        prev.map((index, slotIndex) =>
          isPaused[slotIndex] ? index : (index + 1) % 4,
        ),
      );
    }, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused]);

  const handleCardIndexChange = (slotIndex: number) => (newIndex: number) => {
    setCardIndices((prev) => {
      const newIndices = [...prev];
      newIndices[slotIndex] = newIndex;
      return newIndices;
    });
    setIsPaused((prev) => {
      const newPaused = [...prev];
      newPaused[slotIndex] = true;
      return newPaused;
    });
  };

  const handleTogglePause = (slotIndex: number) => () => {
    setIsPaused((prev) => {
      const newPaused = [...prev];
      newPaused[slotIndex] = !newPaused[slotIndex];
      return newPaused;
    });
  };

  const {
    data: dashboardSlots,
    isLoading: isLoadingSlots,
    isFetched: isFetchedSlots,
  } = useDashboardProjectSlots();
  const { data: projects = [], isFetched: isFetchedProjects } = useProjects();

  const selectedProjects = useMemo(() => {
    if (!dashboardSlots) {
      return [null, null, null, null] as (ProjectWithContractors | null)[];
    }

    const projectMap = new Map(
      projects.map((project) => [project.id, project]),
    );

    return [
      dashboardSlots.slot_0_project_id
        ? projectMap.get(dashboardSlots.slot_0_project_id) || null
        : null,
      dashboardSlots.slot_1_project_id
        ? projectMap.get(dashboardSlots.slot_1_project_id) || null
        : null,
      dashboardSlots.slot_2_project_id
        ? projectMap.get(dashboardSlots.slot_2_project_id) || null
        : null,
      dashboardSlots.slot_3_project_id
        ? projectMap.get(dashboardSlots.slot_3_project_id) || null
        : null,
    ] as (ProjectWithContractors | null)[];
  }, [dashboardSlots, projects]);

  const isActiveStates = useMemo(() => {
    if (!dashboardSlots) {
      return [true, true, true, true];
    }
    return [
      dashboardSlots.slot_0_is_active,
      dashboardSlots.slot_1_is_active,
      dashboardSlots.slot_2_is_active,
      dashboardSlots.slot_3_is_active,
    ];
  }, [dashboardSlots]);

  if (isLoadingSlots) {
    return <EmptyProjectSlideCards showPopoverTrigger={false} />;
  }

  return selectedProjects.some((project) => project !== null) ||
    !isFetchedProjects ||
    !isFetchedSlots ? (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
      {[0, 1, 2, 3].map((index) => {
        return (
          <div className="h-[293px] overflow-clip" key={index}>
            {selectedProjects[index] && isActiveStates[index] ? (
              <DashboardProjectSlotCard
                project={selectedProjects[index]}
                slotIndex={index}
                currentCardIndex={cardIndices[index]}
                onCardIndexChange={handleCardIndexChange(index)}
                isActive={isActiveStates[index]}
                isPaused={isPaused[index]}
                onTogglePause={handleTogglePause(index)}
              />
            ) : (
              <DashboardProjectSelector
                slotIndex={index}
                project={selectedProjects[index]}
                isActive={isActiveStates[index]}
                trigger={<ProjectSlideCardEmpty />}
              />
            )}
          </div>
        );
      })}
    </div>
  ) : (
    <EmptyProjectSlideCards showPopoverTrigger={true} />
  );
};
